<template>
  <div class="q-pa-md">
    <div
      v-if="currentComplexId"
      class="text-center text-h5 q-pa-md"
    >
      Update Complex
    </div>
    <div
      v-else
      align="center"
      class="text-center text-h5 q-pa-md"
    >
      Add New Complex
    </div>
    <div
      class="flex flex-center"
      padding
    >
      <q-form
        ref="validateForm"
        @submit="onSubmit"
        style="width: 600px"
        greedy
      >
        <div class="row">
          <q-input
            v-model.trim="complexDetails.name"
            class="col-5 q-mr-md"
            label="Complex Name"
            :rules="[checkInput, checkIsValidDescription, checkComplexNameDuplicates]"
            lazy-rules
            :borderless="isRa"
            :readonly="isRa"
          />
          <q-input
            v-model="complexDetails.complexCode"
            class="col-5 q-mr-md"
            label="Complex Code"
            :rules="[checkIsValidComplexCode, checkInput, checkComplexCodeDuplicates]"
            lazy-rules
            mask="X"
            reverse-fill-mask
            :readonly="(isRa || (currentComplexId && !hasRoles('R&D'))) ? '' : false"
            :borderless="isRa"
            debounce="1000"
            @update:model-value="hasBuildingInColleague(complexDetails.complexCode)"
          />
          <q-icon
            name="sync_alt"
            :color="isBuildingInColleague ? 'green' : 'grey'"
            class="q-mt-lg"
            size="sm"
          >
            <q-tooltip>
              Exists in Colleague when green.
            </q-tooltip>
          </q-icon>
        </div>
        <q-input
          v-model.trim="complexDetails.addressName"
          label="Address 1"
          :rules="[checkInput]"
          lazy-rules
          :borderless="isRa"
          :readonly="isRa"
        />
        <q-input
          v-model.trim="complexDetails.addressName2"
          label="Address 2"
          :borderless="isRa"
          :readonly="isRa"
        />
        <q-input
          v-model.trim="complexDetails.cityName"
          class="q-mr-md"
          label="City"
          :rules="[checkInput]"
          lazy-rules
          :borderless="isRa"
          :readonly="isRa"
        />
        <div class="row">
          <q-select
            v-model="complexDetails.stateCode"
            class="col-5 q-mr-md"
            :options="statesOptions"
            label="State"
            :rules="[checkSelect]"
            lazy-rules
            :borderless="isRa"
            :readonly="isRa"
            :hide-dropdown-icon="isRa"
          />
          <q-input
            v-model="complexDetails.zipCode"
            class="col-5 q-mr-md"
            label="Zip Code"
            :rules="[checkInput]"
            lazy-rules
            :borderless="isRa"
            :readonly="isRa"
          />
        </div>
        <div class="row">
          <q-input
            v-model="complexDetails.contactName"
            class="col-5 q-mr-md"
            label="Contact Name"
            :rules="[checkInput]"
            lazy-rules
            :borderless="isRa"
            :readonly="isRa"
          />
          <q-input
            v-model="complexDetails.contactPhone"
            class="col-5 q-mr-md"
            label="Contact Phone"
            :rules="[checkIsValidUsaPhone]"
            lazy-rules
            mask="(###) ### - ####"
            unmasked-value
            hint="Mask: (###) ### - ####"
            :borderless="isRa"
            :readonly="isRa"
          />
        </div>
        <div class="row">

          <q-input
            v-model="complexDetails.contactEmail"
            class="col-5 q-mr-md"
            type="email"
            label="Contact Email"
            :rules="[checkIsValidEmail]"
            lazy-rules
            :borderless="isRa"
            :readonly="isRa"
          />
          <q-input
            v-if="!currentComplexId"
            v-model="confirmEmail"
            class="col-5 q-mr-md"
            type="email"
            label="Confirm Contact Email"
            :rules="[val => confirmEmail == complexDetails.contactEmail
            || 'Confirmation email does not match']"
            lazy-rules
            :borderless="isRa"
            :readonly="isRa"
          />
        </div>
        <q-uploader
          class="col-11 q-ma-md"
          :url="complexBlobUploadUrl"
          max-files="1"
          accept=".pdf"
          max-file-size=20971520
          auto-upload
          @added="clearExistingPdf('complexBlob')"
          @uploaded="fileUploaded"
        >
          <template v-slot:header="scope">
            <div class="row no-wrap items-center q-pa-sm q-gutter-xs">
              <div class="col">
                <div class="q-uploader__title">Upload complex pdf</div>
              </div>
              <q-btn
                v-if="!complexDetails.complexBlobId"
                type="a"
                icon-right="add_box"
                dense
                flat
              >
                <q-uploader-add-trigger style="z-index: 1" />
                <q-tooltip>Upload complex pdf</q-tooltip>
              </q-btn>
              <q-btn
                v-if="complexDetails.complexBlobId || scope.uploadedFiles.length > 0"
                icon="download"
                @click="exportPdf(
                complexDetails.complexBlobId, 'complexBlob', complexDetails.name)"
                dense
                flat
              >
                <q-tooltip>Download current complex pdf</q-tooltip>
              </q-btn>
            </div>
          </template>
          <template v-slot:list="scope">
            <q-list separator>

              <q-item
                v-for="file in scope.files"
                :key="file.name"
              >
                <q-item-section>
                  <q-item-label class="full-width ellipsis">
                    {{ file.name }}
                  </q-item-label>

                  <q-item-label caption>
                    Status: {{ file.__status }}
                  </q-item-label>

                  <q-item-label caption>
                    {{ file.__sizeLabel }} / {{ file.__progressLabel }}
                  </q-item-label>
                </q-item-section>

                <q-item-section
                  top
                  side
                >
                  <q-btn
                    class="gt-xs"
                    size="12px"
                    flat
                    dense
                    round
                    icon="clear"
                    @click="deleteComplexBlob(scope, file)"
                  ></q-btn>
                </q-item-section>
              </q-item>
              <q-item
                v-for="file in complexBlobs"
                :key="file"
              >
                <q-item-section>
                  <q-item-label class="full-width ellipsis">
                    {{ file }}
                  </q-item-label>

                  <q-item-label caption>
                    Status: uploaded
                  </q-item-label>

                  <!-- <q-item-label caption>
                      {{ file.__sizeLabel }} / {{ file.__progressLabel }}
                    </q-item-label> -->
                </q-item-section>

                <q-item-section
                  top
                  side
                >
                  <q-btn
                    class="gt-xs"
                    size="12px"
                    flat
                    dense
                    round
                    icon="clear"
                    @click="deleteComplexBlob(scope, file)"
                  ></q-btn>
                </q-item-section>
              </q-item>

            </q-list>
          </template>
        </q-uploader>
        <div>
          <template v-if="isSummitComplex(complexDetails.complexCode)">
            <legend class="text-left text-h5 q-mt-md">Amenity Codes</legend>
            <q-separator />
            <div class="q-gutter-md row items-start justify-between"
                 v-for="amenityCode, index in complexDetails.amenityCodes"
                 :key="index">
              <q-input :label="'Amenity Name ' + (index + 1) + ' *'"
                       v-model.trim="amenityCode.name"
                       style="width: 270px"
                       lazy-rules
                       :borderless="isRa"
                       :readonly="isRa"
                       :hide-dropdown-icon="isRa" />
              <q-input v-model.trim="amenityCode.code"
                       :label="'Amenity Code ' + (index + 1) +' *'"
                       style="width: 270px"
                       lazy-rules
                       :borderless="isRa"
                       :readonly="isRa"
                       :hide-dropdown-icon="isRa" />
              <q-btn v-if="!isRa"
                     @click="removeAmenityCodePair(index)"
                     class="q-mt-xl"
                     icon="remove"
                     color="primary"
                     size="xs"
                     round />
            </div>
            <div class="float-right q-gutter-md">
              <q-btn v-if="!isRa"
                     @click="addAmenityCodePair"
                     icon="add"
                     color="primary"
                     size="xs"
                     class="q-mt-xl"
                     round />
            </div>
          </template>
        </div>
      </q-form>
    </div>
  </div>
</template>

<script>
import { onUnmounted, ref } from 'vue';
import { useQuasar } from 'quasar';
import useAuth from '@/hooks/auth';
import useComplex from '@/hooks/complex';
import useUiUtilities from '@/utilities/uiUtilities';
import useUiValidations from '@/utilities/uiValidations';
import useUtility from '@/hooks/utility';

export default {
  async setup() {
    const $q = useQuasar();
    const { hasRoles } = useAuth();
    const {
      complexDetails,
      createComplex,
      currentComplexId,
      fetchComplex,
      fetchComplexCodesAndNames,
      resetCurrentComplexId,
      updateComplex,
    } = useComplex();
    const {
      checkInput,
      checkSelect,
      checkIsValidComplexCode,
      checkIsValidDescription,
      checkIsValidEmail,
      checkIsValidUsaPhone,
    } = useUiValidations();
    const {
      exportPdf,
      notifyResult,
      statesOptions,
      isShadowbrookComplex,
      isSummitComplex,
    } = useUiUtilities();
    const {
      deleteBlobDocument,
      hasBuildingInColleague,
      isBuildingInColleague,
    } = useUtility();

    onUnmounted(async () => {
      await resetCurrentComplexId();
    });

    const complexCodesAndNames = await fetchComplexCodesAndNames();
    const confirmEmail = ref();
    const validateForm = ref(null);
    const isRa = !hasRoles('HRL', 'R&D') && hasRoles('RESIDENT-ASSISTANT');
    const complexBlobs = ref([]);
    const complexBlobUploadUrl = ref('');

    const onSubmit = async () => {
      const validationResult = await validateForm.value.validate();
      if (validationResult) {
        $q.loading.show();
        let response = '';
        if (currentComplexId.value === undefined || currentComplexId.value === null) {
          response = await createComplex();
        } else {
          response = await updateComplex(currentComplexId.value);
        }
        notifyResult(response.status, response);
        $q.loading.hide();

        return response;
      }

      return 400;
    };
    if (currentComplexId.value) {
      await fetchComplex(currentComplexId.value);
    } else {
      await resetCurrentComplexId();
    }

    const initialComplexName = complexDetails.value.name;
    const initialComplexCode = complexDetails.value.complexCode;
    const checkComplexNameDuplicates = (val) => {
      if (val.trim() === initialComplexName.trim()) {
        return true;
      }
      for (let i = 0; i < complexCodesAndNames.length; i += 1) {
        if (val.trim() === complexCodesAndNames[i].complexName.trim()) {
          return 'Cannot use existing complex name.';
        }
      }

      return true;
    };

    const checkComplexCodeDuplicates = (val) => {
      if (val.trim() === initialComplexCode.trim()) {
        return true;
      }
      for (let i = 0; i < complexCodesAndNames.length; i += 1) {
        if (val.trim() === complexCodesAndNames[i].complexCode) {
          return 'Cannot use existing complex code.';
        }
      }

      return true;
    };

    await hasBuildingInColleague(complexDetails.value.complexCode);

    const updateBlobUrls = () => {
      const blobUrl = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/utility/uploadBlobDocument?`;
      complexBlobUploadUrl.value = complexDetails.value.complexBlobId
        ? `${blobUrl}&blobGeneralId=${complexDetails.value.complexBlobId}&blobName=ComplexBlob`
        : `${blobUrl}&blobName=ComplexBlob`;
    };

    const clearExistingPdf = () => {
      complexBlobs.value = [];
    };

    const fileUploaded = ({ xhr }) => {
      const response = JSON.parse(xhr.response);
      complexDetails.value.complexBlobId = response.item1;
      updateBlobUrls();
    };

    const deleteComplexBlob = async (scope, file) => {
      $q.loading.show();
      clearExistingPdf();
      scope.removeFile(file);
      await deleteBlobDocument(complexDetails.value.complexBlobId);
      complexDetails.value.complexBlobId = null;
      $q.loading.hide();
    };

    updateBlobUrls();
    complexBlobs.value = complexDetails.value.complexBlobId ? [`${complexDetails.value.name} Document.pdf`] : [];

    const addAmenityCodePair = () => {
      if (complexDetails.value.amenityCodes == null) {
        complexDetails.value.amenityCodes = [];
      }

      complexDetails.value.amenityCodes.push({ name: '', code: '' });
    };

    const removeAmenityCodePair = (index) => {
      complexDetails.value.amenityCodes.splice(index, 1);
    };

    return {
      checkComplexCodeDuplicates,
      checkComplexNameDuplicates,
      checkInput,
      checkSelect,
      checkIsValidComplexCode,
      checkIsValidDescription,
      checkIsValidEmail,
      checkIsValidUsaPhone,
      clearExistingPdf,
      complexBlobs,
      complexBlobUploadUrl,
      confirmEmail,
      currentComplexId,
      complexDetails,
      deleteComplexBlob,
      exportPdf,
      fileUploaded,
      hasBuildingInColleague,
      hasRoles,
      isRa,
      isBuildingInColleague,
      onSubmit,
      statesOptions,
      validateForm,
      addAmenityCodePair,
      removeAmenityCodePair,
      isShadowbrookComplex,
      isSummitComplex,
    };
  },
};
</script>
